import { Alert, Avatar, Button, Card, ColorPicker, Flex, Form, GetProp, Input, InputNumber, List, Modal, notification, Popconfirm, Radio, Space, Spin, Tag, Tooltip, Tree, Typography, Upload, UploadProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScenariaService from "../services/scenaria-service";
import { ScenarioModel } from "../models/scenario-model";
import { PollutantModel } from "../models/pollutant-model";
import { AnalysisModel } from "../models/analysis-model";
import Icon, { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, CloseOutlined, EditOutlined, EyeOutlined, InfoCircleOutlined, PlayCircleOutlined, PlusOutlined, SyncOutlined, UploadOutlined } from "@ant-design/icons";
import { title } from "process";
import { PusherPrivateChannel } from "laravel-echo/dist/channel";
import { debounce, groupBy, some } from "lodash";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function ScenariaListPage() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [paginatedScenaria, setPaginatedScenaria] =useState<ScenarioModel[]>([]);
    const [currentPage, setCurrentPage] = useState<number|undefined>(undefined);
    const [totalScenaria, setTotalScenaria] = useState<number|undefined>(undefined);
    const [scenariaPerPage, setScenariaPerPage] = useState<number>(10);
    const [scenarioToView, setScenarioToView] = useState<ScenarioModel|undefined>(undefined);
    const [pollutantToView, setPollutantToView] = useState<PollutantModel|undefined>(undefined);
    const [analysisToAddOrEdit, setAnalysisToView] = useState<AnalysisModel|undefined>(undefined);
    const [isJsonScaleModalOpen, setIsJsonScaleModalOpen] = useState<boolean>(false);
    const [jsonScaleString, setJsonScaleString] = useState<string|undefined>(undefined);
    const [processingAnalyses, setProcessingAnalyses] = useState<AnalysisModel[]>([]);
    const stateRefProcessingAnalyses = useRef(processingAnalyses);
    
    const navigate = useNavigate();
    const scenariaService = ScenariaService.getInstance();

    let analysisChannel : PusherPrivateChannel = window.Echo.private(`Analysis`);
    let generalErrorChannel : PusherPrivateChannel = window.Echo.private(`GeneralBackgroundError`);

    useEffect(()=> {
        analysisChannel.listen('AnalysisChangedEvent', (e:any) => {
            const scenarioId = e.scenario_id as number;
            const pollutantId = e.pollutant_id as number;
            const analysisId = e.id as number;
            scenariaService.getAnalysis(scenarioId, pollutantId, analysisId).subscribe({
                next:(fullAnalysis) => {
                    notification.info({message:`Analysis ${fullAnalysis.title} processing`});
                }
            });
        });

        generalErrorChannel.listen('GeneralBackgroundErrorOccuredEvent', (e:any) => {
            const errorCode = e.error_code;
            if (errorCode === "analysis_job_error") {
                notification.error({message:"Error processing analysis"});
            }
        });
    },[]);

    useEffect(()=>()=> {
        analysisChannel.stopListening('AnalysisChangedEvent');
        generalErrorChannel.stopListening('GeneralBackgroundErrorOccuredEvent');
    },[]);


    useEffect(()=>{
        loadScenaria(1, 10);
        scenariaService.getProcessingAnalysis().subscribe({
            next:(response) => {
                setProcessingAnalyses(response);
                stateRefProcessingAnalyses.current = response;
            }
        });
    },[]);


    const loadScenaria = (page:number, pageSize:number) => {
        setIsLoading(true);
        scenariaService.getScenaria(page, pageSize).subscribe({
            next:(response) => {
                setCurrentPage(response.meta.current_page);
                setScenariaPerPage(response.meta.per_page);
                setPaginatedScenaria(response.data);
                setTotalScenaria(response.meta.total)
                setIsLoading(false);
            },
            error:(error)=> {
                notification.error({message:"Error getting scenaria"});
                setIsLoading(false);
            }
        });
    }

    return(
    <><div style={{display:'flex',flexDirection:'column',height:'100%'}}>
        <div style={{display:'flex',flexDirection:'row',width:'100%'}}>
            <h2 key='title'>Scenaria list</h2>
            <div style={{flex:1}}></div>            
        </div>
        
        <List 
            style={{flex:1,display:'flex',flexDirection:'column',height:`calc(100% - 200px)`}}
            pagination={{
                current:currentPage,
                total:totalScenaria,
                pageSize:scenariaPerPage,
                onChange:(page, pageSize)=> {
                    loadScenaria(page, pageSize);
                }
            }} 
            dataSource={paginatedScenaria}
            renderItem={(item, index) => (
                <List.Item key={index} style={{alignItems:'start'}} extra={[
                    <Button key="1" type="link" icon={<EyeOutlined />} onClick={()=>setScenarioToView({...item} as ScenarioModel)}></Button>,
                ]}>
                    <List.Item.Meta
                        title={<span style={{marginTop:0}}>{item.title} <Button key="2" type="link" style={{marginLeft:20}} icon={<PlayCircleOutlined style={{fontSize:28}} />} disabled={some(item.pollutants, p => some(p.analyses, a=>a.process_status ==='processing' ) )}  onClick={()=>navigate(`/scenaria/${item.id}`)}>Play</Button></span>}
                        description={<>
                            <Typography.Paragraph style={{whiteSpace:'pre-wrap'}} ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}>{item.description}</Typography.Paragraph>
                            {(!item.pollutants || !item.pollutants.length) ? <div>No pollutants added.</div>: 
                            <div style={{display:'flex', flexDirection:'row', width:'100%', overflowX:'auto', gap:20}}>
                                {item.pollutants.map((p,i)=> {
                                    return <Card 
                                        key={i} 
                                        title={<div style={{display:'flex', flexDirection:'row', width:'100%', gap:10}}>
                                            <div style={{flex:1}}>Pollutant: {p.name} {p.description && <Tooltip title={<div style={{whiteSpace:'pre-wrap'}}>{p.description}</div>}><InfoCircleOutlined style={{marginLeft:10}}/> </Tooltip>}</div>
                                            <Button icon={<EyeOutlined/>} onClick={()=>setPollutantToView({...p})}/>
                                        </div>} 
                                        style={{minWidth:500}}>
                                            
                                            {(!p.analyses || !p.analyses.length) ? <Typography.Text type="secondary">No analyses exists for this pollutant.</Typography.Text> : 
                                            Object.values(groupBy(p.analyses ?? [], an=>an.time_step_in_hours)).map((groupOfAnalyses, groupIndex)=> <div key={groupIndex}>
                                                <h4 style={{marginBottom:0,marginTop:groupIndex===0?0:10}} key="title">Time step: {groupOfAnalyses[0].time_step_in_hours ? `${groupOfAnalyses[0].time_step_in_hours} hour(s)`: 'aggregate in period'} </h4>
                                                {groupOfAnalyses.map((a,ai)=> {
                                                    const processingAnalysis = stateRefProcessingAnalyses.current.find(pa => pa.id === a.id);
                                                    if (processingAnalysis) a = processingAnalysis;
                                                    return <div key={ai}>
                                                        <a onClick={()=>setAnalysisToView({...a})}>{ai+1}. {a.title}</a> 
                                                        {a.description && <Tooltip title={<div style={{whiteSpace:'pre-wrap'}}>{a.description}</div>}><InfoCircleOutlined style={{marginLeft:10}}/> </Tooltip>}
                                                        {a.process_status === 'error' && <Tag style={{marginLeft:10}} color='error' icon={<CloseCircleOutlined />}>ERROR</Tag>}
                                                        {a.process_status === 'processing' && <Tag style={{marginLeft:10}} color='processing' icon={<SyncOutlined spin />}>PROCESSING</Tag>}
                                                        {a.process_status === 'completed' && <Tag style={{marginLeft:10}} color='success' icon={<CheckCircleOutlined />}>COMPLETED</Tag>}
                                                        {a.process_status === 'not_started' && <Tag style={{marginLeft:10}} color='default' icon={<ClockCircleOutlined />}>NOT STARTED</Tag>}                                                        
                                                    </div>;
                                                })}
                                            </div>)
                                            }
                                    </Card>
                                })}
                            </div>}
                        </>
                        }
                    />
                </List.Item>
            )}>
        </List>
    </div>

    <Modal 
        title='View scenario'
        closable={false}
        open={scenarioToView !== undefined} 
        footer={<Button onClick={()=>setScenarioToView(undefined)}>Close</Button>}
    >
        {scenarioToView && <Form layout="vertical">
            <Form.Item
                label="Title"
            >
                {scenarioToView.title}
            </Form.Item>
            <Form.Item
                label="Description"
            >
                <div style={{whiteSpace:'pre-wrap'}}>{scenarioToView.description ?? "-"}</div>
            </Form.Item>
        </Form>}
    </Modal>



    <Modal 
        title='View pollutant'
        closable={false}
        open={pollutantToView !== undefined} 
        footer= {<Button onClick={()=>setPollutantToView(undefined)}>Close</Button> }
    >
        {pollutantToView && <Form layout="vertical">
            <Form.Item
                label="Name"
            >
                {pollutantToView.name}
            </Form.Item>
            <Form.Item
                label="Description"
            >
                {pollutantToView.description ?? '-'}
            </Form.Item>
        </Form>}
    </Modal>


    <Modal 
        title='View analysis'
        closable={false}
        open={analysisToAddOrEdit !== undefined} 
        footer={<Button onClick={()=>setAnalysisToView(undefined)}>Close</Button> }
    >
        {analysisToAddOrEdit && <Form layout="vertical">
            <Form.Item
                label="Name"
            >
                {analysisToAddOrEdit.title}
            </Form.Item>
            <Form.Item
                label="Description"
            >
                {analysisToAddOrEdit.description ?? "-"}
            </Form.Item>
            <Form.Item
                label="Timeseries"
            >
                {analysisToAddOrEdit.is_timeseries ? 'Yes': 'No'}
            </Form.Item>
            {analysisToAddOrEdit.is_timeseries === true && <Form.Item
                label="Time step (hours)"
            >
                {analysisToAddOrEdit.time_step_in_hours}H
            </Form.Item>}
            
            <Form.Item
                label="Units"
            >
                {analysisToAddOrEdit.units}
            </Form.Item>

            <List header="Scale steps" dataSource={analysisToAddOrEdit.scale_definition.scale_items} renderItem={(item, index)=> 
                <div key={index} style={{display:'flex', flexDirection:'row'}}>
                    <div style={{alignContent:'center',marginRight:10}}>{`Scale step ${index+1}:`}</div>
                    <div style={{alignContent:'center',marginRight:10}}>{item.upper_limit.toFixed(2)}</div>
                    <ColorPicker value={item.hex_color} showText disabled />
                </div>
            }>

            </List>
            <Form.Item
                label="DAT file"
            >
                {analysisToAddOrEdit.filename}
            </Form.Item>
        </Form>}
    </Modal> 
    </>);
}

export default ScenariaListPage;